import { useState, useRef } from 'react';
import { TILE_W, TILE_H } from '../config/constants';

// Storage key for custom sprites
const CUSTOM_SPRITES_KEY = 'shopfloor_custom_sprites';

// Load custom sprites from localStorage
export function loadCustomSprites() {
  try {
    const stored = localStorage.getItem(CUSTOM_SPRITES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (e) {
    console.error('Failed to load custom sprites:', e);
    return [];
  }
}

// Save custom sprites to localStorage
function saveCustomSprites(sprites) {
  try {
    localStorage.setItem(CUSTOM_SPRITES_KEY, JSON.stringify(sprites));
  } catch (e) {
    console.error('Failed to save custom sprites:', e);
  }
}

// Delete a custom sprite by ID
export function deleteCustomSprite(spriteId) {
  const sprites = loadCustomSprites();
  const updated = sprites.filter(s => s.id !== spriteId);
  saveCustomSprites(updated);
  return updated;
}

// Clear all custom sprites
export function clearAllCustomSprites() {
  localStorage.removeItem(CUSTOM_SPRITES_KEY);
  return [];
}

// Process image to fit isometric tile
async function processImage(file, options = {}) {
  const {
    footprintW = 1,
    footprintH = 1,
    maxSize = 256,
  } = options;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        // Calculate target dimensions based on footprint
        const targetW = Math.min(TILE_W * footprintW * 2, maxSize);
        const targetH = Math.min(TILE_H * footprintH * 2 + targetW / 2, maxSize);

        // Create canvas for processing
        const canvas = document.createElement('canvas');
        canvas.width = targetW;
        canvas.height = targetH;
        const ctx = canvas.getContext('2d');

        // Fill with transparency
        ctx.clearRect(0, 0, targetW, targetH);

        // Calculate scaling to fit while maintaining aspect ratio
        const scale = Math.min(targetW / img.width, targetH / img.height);
        const scaledW = img.width * scale;
        const scaledH = img.height * scale;

        // Center the image
        const offsetX = (targetW - scaledW) / 2;
        const offsetY = (targetH - scaledH) / 2;

        // Draw scaled image
        ctx.drawImage(img, offsetX, offsetY, scaledW, scaledH);

        // Convert to base64
        const dataUrl = canvas.toDataURL('image/png', 0.9);
        
        resolve({
          dataUrl,
          width: targetW,
          height: targetH,
          originalName: file.name,
        });
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export default function SpriteImporter({ onImport, onClose }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [name, setName] = useState('');
  const [category, setCategory] = useState('custom');
  const [footprintW, setFootprintW] = useState(1);
  const [footprintH, setFootprintH] = useState(1);
  const [color, setColor] = useState('#6366f1');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // Validate file type
    if (!selectedFile.type.startsWith('image/')) {
      setError('Please select an image file (PNG, JPG, etc.)');
      return;
    }

    setFile(selectedFile);
    setError(null);

    // Generate preview and auto-calculate footprint
    const reader = new FileReader();
    reader.onload = (loadEvent) => {
      const dataUrl = loadEvent.target.result;
      setPreview(dataUrl);
      
      // Load image to get dimensions for auto-measurement
      const img = new Image();
      img.onload = () => {
        // Conservative auto-calculation:
        // Most machines are 2x1, 2x2, or 3x1
        // Start small and let user adjust up
        
        const aspectRatio = img.width / img.height;
        
        let estimatedW, estimatedH;
        
        if (aspectRatio > 2) {
          // Very wide image (like a press brake) - probably 3x1 or 4x1
          estimatedW = 3;
          estimatedH = 1;
        } else if (aspectRatio > 1.2) {
          // Wide image - probably 2x1
          estimatedW = 2;
          estimatedH = 1;
        } else if (aspectRatio < 0.8) {
          // Tall/deep image - probably 1x2
          estimatedW = 1;
          estimatedH = 2;
        } else {
          // Square-ish - default 2x2
          estimatedW = 2;
          estimatedH = 2;
        }
        
        setFootprintW(estimatedW);
        setFootprintH(estimatedH);
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(selectedFile);

    // Auto-generate name from filename
    const baseName = selectedFile.name.replace(/\.[^/.]+$/, '');
    setName(baseName.replace(/[-_]/g, ' '));
  };

  const handleImport = async () => {
    if (!file || !name.trim()) {
      setError('Please select a file and enter a name');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Process the image
      const processed = await processImage(file, {
        footprintW,
        footprintH,
      });

      // Create sprite definition
      const spriteId = `custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const newSprite = {
        id: spriteId,
        name: name.trim(),
        category,
        footprint: { w: footprintW, h: footprintH },
        color: parseInt(color.replace('#', ''), 16),
        sprite: processed.dataUrl,
        createdAt: new Date().toISOString(),
      };

      // Load existing sprites and add new one
      const existingSprites = loadCustomSprites();
      const updatedSprites = [...existingSprites, newSprite];
      saveCustomSprites(updatedSprites);

      // Notify parent
      if (onImport) {
        onImport(newSprite);
      }

      // Close modal
      if (onClose) {
        onClose();
      }
    } catch (err) {
      setError(err.message || 'Failed to process image');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-lg shadow-xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-slate-700 px-4 py-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Import Sprite</h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Image File
            </label>
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-slate-600 rounded-lg p-6 text-center cursor-pointer hover:border-indigo-500 transition-colors"
            >
              {preview ? (
                <div className="space-y-2">
                  <img
                    src={preview}
                    alt="Preview"
                    className="max-h-32 mx-auto object-contain"
                  />
                  <p className="text-sm text-slate-400">{file?.name}</p>
                </div>
              ) : (
                <div className="text-slate-400">
                  <svg className="w-10 h-10 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p>Click to select an image</p>
                  <p className="text-xs mt-1">PNG, JPG, GIF, WebP</p>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., CNC Router"
              className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-indigo-500"
            />
          </div>

          {/* Category & Color */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Category
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-2 text-white focus:outline-none focus:border-indigo-500"
              >
                <option value="machines">Machines</option>
                <option value="props">Props</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Fallback Color
              </label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="w-10 h-10 rounded cursor-pointer border-0"
                />
                <input
                  type="text"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="flex-1 bg-slate-700 border border-slate-600 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500"
                />
              </div>
            </div>
          </div>

          {/* Footprint */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Footprint (tiles)
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="1"
                max="10"
                value={footprintW}
                onChange={(e) => setFootprintW(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-20 bg-slate-700 border border-slate-600 rounded px-3 py-2 text-white text-center focus:outline-none focus:border-indigo-500"
              />
              <span className="text-slate-400">×</span>
              <input
                type="number"
                min="1"
                max="10"
                value={footprintH}
                onChange={(e) => setFootprintH(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-20 bg-slate-700 border border-slate-600 rounded px-3 py-2 text-white text-center focus:outline-none focus:border-indigo-500"
              />
              <span className="text-sm text-slate-400 ml-2">
                ({footprintW * footprintH} tile{footprintW * footprintH > 1 ? 's' : ''})
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Adjust to match the base/floor area of your sprite
            </p>
          </div>

          {/* Tile Preview */}
          {preview && (
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Tile Preview
              </label>
              <div className="bg-slate-900 rounded-lg p-4 flex items-center justify-center">
                <TilePreview 
                  src={preview} 
                  footprintW={footprintW} 
                  footprintH={footprintH}
                />
              </div>
              {/* Quick select common sizes */}
              <div className="flex flex-wrap gap-1 mt-2">
                <span className="text-xs text-slate-500 mr-1">Quick:</span>
                {[
                  { w: 1, h: 1, label: '1×1' },
                  { w: 2, h: 1, label: '2×1' },
                  { w: 2, h: 2, label: '2×2' },
                  { w: 3, h: 1, label: '3×1' },
                  { w: 3, h: 2, label: '3×2' },
                  { w: 4, h: 2, label: '4×2' },
                ].map(({ w, h, label }) => (
                  <button
                    key={label}
                    onClick={() => { setFootprintW(w); setFootprintH(h); }}
                    className={`px-2 py-0.5 text-xs rounded transition-colors ${
                      footprintW === w && footprintH === h
                        ? 'bg-indigo-600 text-white'
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-500/20 border border-red-500/50 rounded px-3 py-2 text-red-400 text-sm">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-slate-700/50 px-4 py-3 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-300 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={!file || !name.trim() || processing}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded transition-colors"
          >
            {processing ? 'Processing...' : 'Import Sprite'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Preview component showing sprite on isometric tile
function TilePreview({ src, footprintW, footprintH }) {
  // Calculate isometric diamond points
  const scale = 1.5; // Scale up for visibility
  const tw = TILE_W * scale;
  const th = TILE_H * scale;
  
  // Diamond corners for the footprint (per guidebook):
  // - Diamond top corner is at (0, 0)
  // - Diamond bottom corner is at: ((w-h)*TW/2, (w+h)*TH/2)
  const topX = 0;
  const topY = 0;
  const rightX = footprintW * tw / 2;
  const rightY = footprintW * th / 2;
  const bottomX = (footprintW - footprintH) * tw / 2;
  const bottomY = (footprintW + footprintH) * th / 2;
  const leftX = -footprintH * tw / 2;
  const leftY = footprintH * th / 2;
  
  // Sprite width = tile diagonal width (matching PixiStage scaling)
  const spriteW = (footprintW + footprintH) * tw / 2 * 0.95;
  
  // SVG viewBox - add padding, extra space above for tall sprites
  const padding = 20;
  const minX = Math.min(topX, rightX, bottomX, leftX) - padding;
  const maxX = Math.max(topX, rightX, bottomX, leftX) + padding;
  const minY = -120; // Fixed top to accommodate tall sprites
  const maxY = Math.max(topY, rightY, bottomY, leftY) + padding;
  
  const width = maxX - minX;
  const height = maxY - minY;
  
  // ANCHOR POSITIONING: Sprite bottom-center at diamond's bottom corner
  // (consistent with PixiStage.jsx placement)
  const spriteAnchorX = bottomX;
  const spriteAnchorY = bottomY;
  
  return (
    <svg 
      width="220" 
      height="180" 
      viewBox={`${minX} ${minY} ${width} ${height}`}
      className="overflow-visible"
    >
      {/* Tile fill */}
      <polygon
        points={`${topX},${topY} ${rightX},${rightY} ${bottomX},${bottomY} ${leftX},${leftY}`}
        fill="rgba(99, 102, 241, 0.5)"
        stroke="#6366f1"
        strokeWidth="2"
      />
      
      {/* Sprite image - bottom-center anchored at diamond bottom */}
      <image
        href={src}
        x={spriteAnchorX - spriteW / 2}
        y={spriteAnchorY - 200} // Large height placeholder, preserveAspectRatio aligns bottom
        width={spriteW}
        height="200"
        preserveAspectRatio="xMidYMax meet"
      />
      
      {/* Tile outline on top (gold dashed) */}
      <polygon
        points={`${topX},${topY} ${rightX},${rightY} ${bottomX},${bottomY} ${leftX},${leftY}`}
        fill="none"
        stroke="#fbbf24"
        strokeWidth="2"
        strokeDasharray="6,3"
      />
    </svg>
  );
}
